import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Stack,
  Chip,
  Autocomplete,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Switch,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { createFundThesis, updateFundThesis, getFundThesisVersions, rollbackToVersion, sharedFundsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import VersionDetailsDialog from './VersionDetailsDialog';
import ComparisonResultDialog from './ComparisonResultDialog';
import { RulesEngineTab } from './RulesEngineTab';
import { SharingTab } from '../SharingTab';

// Assuming commonSectors is available from a shared source or passed as a prop
const commonSectors = [
    'Technology', 'Healthcare', 'Financial Services', 'Consumer Discretionary',
    'Industrials', 'Real Estate', 'Energy', 'Utilities', 'Materials',
    'Communication Services'
];


interface FundThesisViewProps {
  fund: { id: string; name: string } | null;
  fundThesis: any; // Replace 'any' with a proper type for fund thesis
  onBack: () => void;
  onSave: (action: () => Promise<any>) => Promise<void>;
  onRollback: (action: () => Promise<any>) => Promise<void>;
}

function a11yProps(index: number) {
    return {
      id: `fund-thesis-tab-${index}`,
      'aria-controls': `fund-thesis-tabpanel-${index}`,
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
  
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`fund-thesis-tabpanel-${index}`}
        aria-labelledby={`fund-thesis-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
}


const FundThesisView: React.FC<FundThesisViewProps> = ({ fund, fundThesis, onBack, onSave, onRollback }) => {
    const { user, currentOrganization } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [saving, setSaving] = useState(false);

    const isPrivilegedUser = React.useMemo(() => {
        const role = currentOrganization?.organization_role?.toLowerCase();
        return role === 'admin' || role === 'owner';
    }, [currentOrganization]);

    // Form State
    const [thesisName, setThesisName] = useState('');
    const [thesisDescription, setThesisDescription] = useState('');
    const [coreInvestmentThemes, setCoreInvestmentThemes] = useState<string[]>([]);
    const [valueCreationLevers, setValueCreationLevers] = useState<string[]>([]);
    const [dealBreakers, setDealBreakers] = useState<string[]>([]);
    const [preferredBusinessModels, setPreferredBusinessModels] = useState<string[]>([]);
    const [thesisSectorFocus, setThesisSectorFocus] = useState<string[]>([]);
    const [industryFocus, setIndustryFocus] = useState<string[]>([]);
    const [riskAppetite, setRiskAppetite] = useState<'Low' | 'Medium' | 'High'>('Medium');

    // Dialog State
    const [addItemDialog, setAddItemDialog] = useState(false);
    const [addItemType, setAddItemType] = useState<'theme' | 'dealBreaker' | 'macroTrend' | 'microTrend' | ''>('');
    const [newItemValue, setNewItemValue] = useState('');
    const [selectedVersion, setSelectedVersion] = useState<any | null>(null);
    const [isVersionDialogPoppedUp, setIsVersionDialogPoppedUp] = useState(false);
 
    // Versioning State
    const [versions, setVersions] = useState<any[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [comparisonData, setComparisonData] = useState<{ version1: any; version2: any } | null>(null);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);
    const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
    const [isRollingBack, setIsRollingBack] = useState(false);

     useEffect(() => {
         if (fundThesis) {
            setThesisName(fundThesis.name || '');
            setThesisDescription(fundThesis.description || '');
            setCoreInvestmentThemes(fundThesis.core_investment_themes || []);
            setValueCreationLevers(fundThesis.value_creation_levers || []);
            setDealBreakers(fundThesis.deal_breakers || []);
            setPreferredBusinessModels(fundThesis.preferred_business_models || []);
            setThesisSectorFocus(fundThesis.sector_focus || []);
            setIndustryFocus(fundThesis.industry_focus || []);
            const riskAppetiteValue = fundThesis.risk_appetite ? fundThesis.risk_appetite.charAt(0).toUpperCase() + fundThesis.risk_appetite.slice(1) : 'Medium';
            setRiskAppetite(riskAppetiteValue as 'Low' | 'Medium' | 'High');
        } else {
            // Reset form for new thesis
            setThesisName(fund ? `${fund.name} Investment Thesis` : '');
            setThesisDescription('');
            setCoreInvestmentThemes([]);
            setValueCreationLevers([]);
            setDealBreakers([]);
            setPreferredBusinessModels([]);
            setThesisSectorFocus([]);
            setIndustryFocus([]);
            setRiskAppetite('Medium');
        }
    }, [fundThesis, fund]);

    useEffect(() => {
        if (tabValue === 2 && fundThesis?.id) {
            const fetchVersions = async () => {
                setLoadingVersions(true);
                try {
                    const data = await getFundThesisVersions(fundThesis.id);
                    setVersions(data);
                } catch (error) {
                    console.error('Error fetching versions:', error);
                } finally {
                    setLoadingVersions(false);
                }
            };
            fetchVersions();
        }
    }, [tabValue, fundThesis?.id]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleAddItem = (type: 'theme' | 'dealBreaker' | 'macroTrend' | 'microTrend') => {
        setAddItemType(type);
        setNewItemValue('');
        setAddItemDialog(true);
    };

    const confirmAddItem = () => {
        if (newItemValue.trim()) {
            switch (addItemType) {
                case 'theme':
                    setCoreInvestmentThemes([...coreInvestmentThemes, newItemValue.trim()]);
                    break;
                case 'dealBreaker':
                    setDealBreakers([...dealBreakers, newItemValue.trim()]);
                    break;
                // Add cases for macro/micro trends if needed
                default:
                    break;
            }
        }
        setAddItemDialog(false);
        setNewItemValue('');
    };

    const saveFundThesis = async () => {
        if (!fund) return;

        setSaving(true);
        const thesisData = {
            name: thesisName,
            description: thesisDescription,
            core_investment_themes: coreInvestmentThemes,
            value_creation_levers: valueCreationLevers,
            deal_breakers: dealBreakers,
            preferred_business_models: preferredBusinessModels,
            macro_trends: [], // Add state for these if needed
            micro_trends: [], // Add state for these if needed
            risk_appetite: riskAppetite.toLowerCase(),
            sector_focus: thesisSectorFocus,
            industry_focus: industryFocus,
            is_global: false,
            fund_id: fund.id,
        };

        try {
            if (fundThesis && fundThesis.id) {
                await updateFundThesis(fund.id, fundThesis.id, thesisData);
            } else {
                await createFundThesis(fund.id, thesisData);
            }
            await onSave(async () => {
                if (fundThesis && fundThesis.id) {
                    await updateFundThesis(fund.id, fundThesis.id, thesisData);
                } else {
                    await createFundThesis(fund.id, thesisData);
                }
            });
        } catch (error) {
            console.error('Failed to save fund thesis:', error);
            // The error is already handled by the parent component's handleThesisAction
        } finally {
            setSaving(false);
        }
    };

    const handleViewVersionDetails = (version: any) => {
        setSelectedVersion(version);
        setIsVersionDialogPoppedUp(true);
    };

    const handleCompareVersions = () => {
        if (selectedVersions.length !== 2) {
            window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'Please select exactly two versions to compare.' }));
            return;
        }

        const version1 = versions.find(v => v.id === selectedVersions[0]);
        const version2 = versions.find(v => v.id === selectedVersions[1]);

        if (!version1 || !version2) {
            window.dispatchEvent(new CustomEvent('apiError', { detail: 'Could not find selected versions.' }));
            return;
        }

        setComparisonData({ version1, version2 });
        setIsComparisonOpen(true);
    };

    const handleVersionSelection = (versionId: string) => {
        setSelectedVersions(prev => {
            if (prev.includes(versionId)) {
                return prev.filter(id => id !== versionId);
            }
            if (prev.length < 2) {
                return [...prev, versionId];
            }
            window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only select up to two versions to compare.' }));
            return prev;
        });
    };

    const handleRollback = (versionId: string) => {
        setSelectedVersionId(versionId);
        setRollbackDialogOpen(true);
    };

    const confirmRollback = async () => {
        if (!fundThesis?.id || !selectedVersionId) return;
        setIsRollingBack(true);
        try {
            await rollbackToVersion(fundThesis.id, selectedVersionId);
            window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Rollback successful. A new version has been created.' }));
            const data = await getFundThesisVersions(fundThesis.id);
            setVersions(data);
            await onRollback(async () => {
                await rollbackToVersion(fundThesis.id, selectedVersionId);
            });
        } catch (error) {
            console.error('Error rolling back:', error);
            // The error is already handled by the parent component's handleThesisAction
        } finally {
            setRollbackDialogOpen(false);
            setSelectedVersionId(null);
            setIsRollingBack(false);
        }
    };

    const selectedFundName = fund?.name || '...';

    return (
        <Box sx={{ flexGrow: 1 }}>
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Fund Investment Thesis - {selectedFundName}</Typography>
            {/* <Button
              startIcon={<BackIcon />}
              onClick={onBack}
              variant="outlined"
              size="small"
            >
              Back to Main Menu
            </Button> */}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="fund thesis tabs">
              <Tab label="Thesis Settings" {...a11yProps(0)} />
              <Tab label="Rules Engine" {...a11yProps(1)} />
              <Tab label="Version History" {...a11yProps(2)} />
              {fundThesis?.id && (user?.id === fundThesis?.created_by || currentOrganization?.organization_role === 'Admin' || currentOrganization?.organization_role === 'Owner') && <Tab label="Sharing" {...a11yProps(3)} />}
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>📁 {selectedFundName} Investment Thesis</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Define the specific investment strategy for this fund, customized to its unique focus and objectives.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 3 }}>
              <TextField
                fullWidth
                label="Thesis Name"
                value={thesisName}
                onChange={(e) => setThesisName(e.target.value)}
                placeholder={`e.g., ${selectedFundName} Investment Thesis`}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={thesisDescription}
                onChange={(e) => setThesisDescription(e.target.value)}
                placeholder="Describe this fund's specific investment philosophy and approach..."
              />

              {/* Core Investment Themes */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Core Investment Themes</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define the key themes that drive this fund's investment decisions.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  {coreInvestmentThemes.map((theme, index) => (
                    <Chip
                      key={index}
                      label={theme}
                      onDelete={() => setCoreInvestmentThemes(coreInvestmentThemes.filter((_, i) => i !== index))}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  <Chip
                    icon={<AddIcon />}
                    label="Add Theme"
                    variant="outlined"
                    onClick={() => handleAddItem('theme')}
                    sx={{ m: 0.5 }}
                  />
                </Stack>
              </Box>

              {/* Value Creation Levers */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Value Creation Levers</Typography>
                <Autocomplete
                  multiple
                  options={['Operational Efficiency', 'Market Expansion', 'Product Development', 'M&A Integration', 'Talent Acquisition', 'Technology Upgrade', 'Cost Optimization', 'Revenue Growth', 'Digital Transformation', 'ESG Implementation']}
                  value={valueCreationLevers}
                  onChange={(_, newValue) => setValueCreationLevers(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select value creation levers" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Box>

              {/* Deal Breakers */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Deal Breakers</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define absolute criteria that would disqualify an investment opportunity for this fund.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  {dealBreakers.map((breaker, index) => (
                    <Chip
                      key={index}
                      label={breaker}
                      onDelete={() => setDealBreakers(dealBreakers.filter((_, i) => i !== index))}
                      color="error"
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  <Chip
                    icon={<AddIcon />}
                    label="Add Deal Breaker"
                    variant="outlined"
                    onClick={() => handleAddItem('dealBreaker')}
                    sx={{ m: 0.5 }}
                  />
                </Stack>
              </Box>

              {/* Preferred Business Models */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Preferred Business Models</Typography>
                <Autocomplete
                  multiple
                  options={['SaaS', 'Marketplace', 'Subscription', 'E-commerce', 'B2B Services', 'Manufacturing', 'Asset-Light', 'Platform', 'Franchise', 'Licensing', 'Freemium', 'Direct-to-Consumer']}
                  value={preferredBusinessModels}
                  onChange={(_, newValue) => setPreferredBusinessModels(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select preferred business models" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Box>

              {/* Sector Focus */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Sector Focus</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define specific sectors this fund thesis focuses on.
                </Typography>
                <Autocomplete
                  multiple
                  options={commonSectors}
                  value={thesisSectorFocus}
                  onChange={(_, newValue) => setThesisSectorFocus(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select sectors" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Box>

              {/* Industry Focus */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Industry Focus</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define specific industries within the selected sectors.
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[
                    'Software & Technology', 'Healthcare Services', 'Financial Technology',
                    'Real Estate Technology', 'Clean Energy', 'Consumer Products',
                    'Industrial Automation', 'Telecommunications Infrastructure',
                    'Digital Media', 'Transportation & Logistics', 'EdTech',
                    'AgTech', 'E-commerce Platforms', 'Advanced Manufacturing',
                    'Biotechnology & Life Sciences'
                  ]}
                  value={industryFocus}
                  onChange={(_, newValue) => setIndustryFocus(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or type industries" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Box>

              {/* Risk Appetite */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>Risk Appetite</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define this fund's risk tolerance for investments.
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={riskAppetite}
                    onChange={(e) => setRiskAppetite(e.target.value as 'Low' | 'Medium' | 'High')}
                  >
                    <FormControlLabel
                      value="Low"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">Low - Conservative</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Established companies, proven business models
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="Medium"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">Medium - Balanced</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mix of growth and stability
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="High"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">High - Aggressive</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Early-stage, high-growth potential
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={onBack}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveFundThesis}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Fund Thesis'}
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <RulesEngineTab thesisId={fundThesis?.id} isPrivilegedUser={isPrivilegedUser} disableAddRule={!fundThesis?.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h5" gutterBottom>📋 Version History</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Track changes to this fund's thesis over time.
            </Typography>

            {!fundThesis?.id ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please save the thesis to view version history.
              </Alert>
            ) : loadingVersions ? (
              <CircularProgress />
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Thesis Versions</Typography>
                  <Button
                    variant="contained"
                    onClick={handleCompareVersions}
                    disabled={selectedVersions.length !== 2}
                  >
                    Compare Selected
                  </Button>
                </Box>
                <Paper variant="outlined">
                  <List>
                    {versions.map((version, index) => (
                      <React.Fragment key={version.id}>
                        <ListItem>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Switch
                              checked={selectedVersions.includes(version.id)}
                              onChange={() => handleVersionSelection(version.id)}
                              size="small"
                            />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1">Version {version.version_number}</Typography>
                                  {index === 0 && <Chip label="Current" color="primary" size="small" />}
                                </Box>
                              }
                              secondary={`Updated on ${new Date(version.created_at).toLocaleDateString()} by ${version.creator?.email || 'N/A'}`}
                            />
                          </Stack>
                          <Box sx={{ flexGrow: 1 }} />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton edge="end" aria-label="view" onClick={() => handleViewVersionDetails(version)}>
                              <ViewIcon />
                            </IconButton>
                            <Tooltip title="Rollback to this version">
                              <span>
                                <IconButton
                                  edge="end"
                                  aria-label="rollback"
                                  onClick={() => handleRollback(version.id)}
                                  disabled={index === 0}
                                >
                                  <HistoryIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </ListItem>
                        {index < versions.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </>
            )}
          </TabPanel>

          {fundThesis?.id && (user?.id === fundThesis?.created_by || currentOrganization?.organization_role === 'Admin' || currentOrganization?.organization_role === 'Owner') && (
            <TabPanel value={tabValue} index={3}>
              {fund?.id ? (
                <SharingTab
                  entityId={fund.id}
                  sharingApi={{
                    getSharedWithUsers: sharedFundsApi.getSharedWithUsers,
                    share: (params) => sharedFundsApi.shareFund({ fund_id: params.fund_id || '', shared_with_id: params.shared_with_id }),
                    unshare: sharedFundsApi.unshareFund,
                  }}
                  entityName="fund"
                />
              ) : (
                <Alert severity="info">Fund not available for sharing.</Alert>
              )}
            </TabPanel>
          )}
        </Paper>

        {/* Add Item Dialog */}
        <Dialog open={addItemDialog} onClose={() => setAddItemDialog(false)}>
          <DialogTitle>
            Add {addItemType === 'theme' ? 'Investment Theme' :
                 addItemType === 'dealBreaker' ? 'Deal Breaker' :
                 addItemType === 'macroTrend' ? 'Macro Trend' :
                 addItemType === 'microTrend' ? 'Micro Trend' : 'Item'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={addItemType === 'theme' ? 'Theme Name' :
                     addItemType === 'dealBreaker' ? 'Deal Breaker' :
                     addItemType === 'macroTrend' ? 'Macro Trend' :
                     addItemType === 'microTrend' ? 'Micro Trend' : 'Item'}
              fullWidth
              variant="outlined"
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              placeholder={
                addItemType === 'theme' ? 'e.g., Digital Transformation, Sustainability' :
                addItemType === 'dealBreaker' ? 'e.g., Regulatory issues, Poor management' :
                addItemType === 'macroTrend' ? 'e.g., Remote work adoption, ESG focus' :
                addItemType === 'microTrend' ? 'e.g., AI in healthcare, Fintech disruption' : ''
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddItemDialog(false)}>Cancel</Button>
            <Button
              onClick={confirmAddItem}
              variant="contained"
              disabled={!newItemValue.trim()}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <VersionDetailsDialog
            open={isVersionDialogPoppedUp}
            onClose={() => setIsVersionDialogPoppedUp(false)}
            version={selectedVersion}
            viewType="fund"
        />

        <ComparisonResultDialog
            open={isComparisonOpen}
            onClose={() => setIsComparisonOpen(false)}
            version1={comparisonData?.version1}
            version2={comparisonData?.version2}
        />

        <Dialog open={rollbackDialogOpen} onClose={() => setRollbackDialogOpen(false)}>
            <DialogTitle>Confirm Rollback</DialogTitle>
            <DialogContent>
                <Typography>
                    Are you sure you want to roll back to this version? This action will create a new version with the content of the selected version.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setRollbackDialogOpen(false)} disabled={isRollingBack}>Cancel</Button>
                <Button onClick={confirmRollback} color="primary" disabled={isRollingBack}>
                    {isRollingBack ? <CircularProgress size={24} /> : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
      </Box>
    );
};

export default FundThesisView;